import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/los";
import { LeafToggleSwitch } from "../../../../common/leaf/LeafToggleSwitch/LeafToggleSwitch";
import { IconCheck } from "../../../../common/icons/IconCheck/IconCheck";
import { IconClose } from "../../../../common/icons/IconClose/IconClose";
import { useFullscreenPortalTarget } from "../../../../common/util/fullscreenPortalTarget";
import "./LosFilterModal.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LosFilterState {
  /** true = "Show International Frontlines Only" is ON */
  showInternationalFrontlines: boolean;
  /** true = "Show In-Market Frontlines Only" is ON (mutually exclusive with showInternationalFrontlines) */
  showInMarketFrontlines: boolean;
  /** true = "Show International Downlines" is ON (available when either frontlines toggle is ON) */
  showInternationalDownlines: boolean;
  /** true = "Show Data" (volume) is ON */
  showData: boolean;
  /** true = "Show ABOs With Zero Volume" is ON */
  showABOsWithZeroVolume: boolean;
}

/**
 * Default state: "Show In-Market Frontlines Only", "Show Data", "Show ABOs With Zero Volume" are ON.
 * Apply button starts disabled (draft == committed).
 */
export const DEFAULT_FILTER_STATE: LosFilterState = {
  showInternationalFrontlines: false,
  showInMarketFrontlines: true,
  showInternationalDownlines: false,
  showData: true,
  showABOsWithZeroVolume: true,
};

// ─── countActiveFilters helper ────────────────────────────────────────────────

/**
 * Counts the number of toggles that are currently ON (true).
 * Used to display the badge count on the filter button — the badge reflects
 * how many filters are active at any given time.
 */
export const countActiveFilters = (f: LosFilterState): number => {
  let count = 0;
  if (f.showInternationalFrontlines) count++;
  if (f.showInMarketFrontlines) count++;
  if (f.showInternationalDownlines) count++;
  if (f.showData) count++;
  if (f.showABOsWithZeroVolume) count++;
  return count;
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LosUiHeaderFilterFlags {
  /** Coarse flag — enables all three international rows when no granular flag is set */
  uiEnableFilterInternational?: boolean;
  /** Granular flag — Row 1 "Show International Frontlines Only" */
  uiEnableFilterInternationalFrontlines?: boolean;
  /** Granular flag — Row 2 "Show In-Market Frontlines Only" */
  uiEnableFilterInMarketFrontlines?: boolean;
  /** Granular flag — Row 3 "Show International Downlines" */
  uiEnableFilterInternationalDownlines?: boolean;
  uiEnableFilterVolume?: boolean;
  uiEnableFilterZeroVolume?: boolean;
}

export interface InternationalFilterVisibility {
  showIntlFrontlines: boolean;
  showInMarketFrontlines: boolean;
  showIntlDownlines: boolean;
  /**
   * true when the in-market row is the only international row — it is shown locked ON,
   * since turning it OFF would switch to an international view the market doesn't allow.
   */
  lockInMarketFrontlines: boolean;
}

// ─── resolveInternationalFilterFlags helper ───────────────────────────────────

/** true when the market configures any granular international filter flag. */
export const hasGranularInternationalFilterFlags = (uiHeader: LosUiHeaderFilterFlags | undefined): boolean =>
  uiHeader?.uiEnableFilterInternationalFrontlines !== undefined ||
  uiHeader?.uiEnableFilterInMarketFrontlines !== undefined ||
  uiHeader?.uiEnableFilterInternationalDownlines !== undefined;

/**
 * Resolves which international filter rows are visible.
 *
 * Granular-overrides-coarse: when any granular international flag is set, only the
 * granular flags apply and the coarse uiEnableFilterInternational is ignored. Otherwise
 * the coarse flag enables all three rows (existing behaviour).
 */
export const resolveInternationalFilterFlags = (
  uiHeader: LosUiHeaderFilterFlags | undefined,
): InternationalFilterVisibility => {
  const flags = uiHeader ?? {};
  const hasAnyGranularFlag = hasGranularInternationalFilterFlags(flags);

  const showIntlFrontlines = hasAnyGranularFlag
    ? !!flags.uiEnableFilterInternationalFrontlines
    : !!flags.uiEnableFilterInternational;
  const showInMarketFrontlines = hasAnyGranularFlag
    ? !!flags.uiEnableFilterInMarketFrontlines
    : !!flags.uiEnableFilterInternational;
  const showIntlDownlines = hasAnyGranularFlag
    ? !!flags.uiEnableFilterInternationalDownlines
    : !!flags.uiEnableFilterInternational;

  return {
    showIntlFrontlines,
    showInMarketFrontlines,
    showIntlDownlines,
    lockInMarketFrontlines: showInMarketFrontlines && !showIntlFrontlines && !showIntlDownlines,
  };
};

// ─── hasAtLeastOneActiveFilter helper ─────────────────────────────────────────

/**
 * Returns true when at least one *visible* toggle in the draft state is ON.
 * Used to guard the Apply button — applying with everything OFF is invalid
 * since it would hide all data.
 *
 * Only the rows that are actually rendered are considered: international rows
 * are gated by uiEnableFilterInternational; volume rows are gated by their
 * respective flags and are hidden when inTreeView is true.
 */
export const hasAtLeastOneActiveFilter = (
  f: LosFilterState,
  uiHeader: LosUiHeaderFilterFlags,
  inTreeView: boolean,
): boolean => {
  if (uiHeader.uiEnableFilterInternational) {
    if (f.showInternationalFrontlines) return true;
    if (f.showInMarketFrontlines) return true;
    if (f.showInternationalDownlines) return true;
  }
  if (uiHeader.uiEnableFilterVolume && !inTreeView) {
    if (f.showData) return true;
  }
  if (uiHeader.uiEnableFilterZeroVolume && !inTreeView) {
    if (f.showABOsWithZeroVolume) return true;
  }
  return false;
};

export interface LosFilterModalProps {
  /** Whether the modal is visible */
  show: boolean;
  /** UI feature flags that gate individual filter rows */
  uiHeader: LosUiHeaderFilterFlags;
  /** The last applied (committed) filter state — draft is seeded from this on open */
  filterState: LosFilterState;
  /** When true, Volume and ZeroVolume rows are hidden */
  inTreeView: boolean;
  /** Called with the new filter state when the user clicks Apply */
  onApply: (next: LosFilterState) => void;
  /** Called when the user dismisses the modal without applying (Cancel / Escape / outside-click) */
  onClose: () => void;
  /** Ref to the trigger button — excluded from outside-click so clicking the button toggles correctly */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const statesEqual = (a: LosFilterState, b: LosFilterState): boolean =>
  a.showInternationalFrontlines === b.showInternationalFrontlines &&
  a.showInMarketFrontlines === b.showInMarketFrontlines &&
  a.showInternationalDownlines === b.showInternationalDownlines &&
  a.showData === b.showData &&
  a.showABOsWithZeroVolume === b.showABOsWithZeroVolume;

// ─── Component ────────────────────────────────────────────────────────────────

export const LosFilterModal = ({
  show,
  uiHeader,
  filterState,
  inTreeView,
  onApply,
  onClose,
  triggerRef,
}: LosFilterModalProps) => {
  const { t } = useTranslation(["los"], { useSuspense: false });
  // Local draft — user edits this; discarded on cancel, seeded from filterState on open
  const [draft, setDraft] = useState<LosFilterState>({ ...filterState });
  // Portal to document body on mobile so the modal escapes the app header's stacking context.
  // On desktop the modal stays in-place (position: absolute relative to LosFilterButton).
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 620px)").matches);
  const portalTarget = useFullscreenPortalTarget();

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 620px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Sync draft with filterState whenever the modal opens
  useEffect(() => {
    if (show) {
      setDraft({ ...filterState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const intlVisibility = resolveInternationalFilterFlags(uiHeader);

  // ── Outside-click handler ────────────────────────────────────────────────────
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!show) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        // Also ignore clicks on the trigger button — it handles its own toggle
        if (triggerRef?.current && triggerRef.current.contains(e.target as Node)) return;
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, onClose]);

  // ── Row 1 — Show International Frontlines Only ────────────────────────────────
  // Mutually exclusive with Row 2 (In-Market).
  // Turning ON auto-enables Row 3 (International Downlines) if it wasn't already ON.
  // Turning OFF switches to In-Market mode; downlines state is preserved.
  const handleIntlFrontlinesChange = (checked: boolean) => {
    setDraft((prev) => ({
      ...prev,
      showInternationalFrontlines: checked,
      showInMarketFrontlines: checked ? false : true, // mutually exclusive
      // auto-enable downlines when switching to international mode; preserve when switching back
      showInternationalDownlines: checked ? true : prev.showInternationalDownlines,
    }));
  };

  // ── Row 2 — Show In-Market Frontlines Only ────────────────────────────────────
  // Mutually exclusive with Row 1. Turning OFF auto-activates International Frontlines.
  // Downlines state is preserved when switching between modes.
  const handleInMarketFrontlinesChange = (checked: boolean) => {
    // Locked ON — never switch to international mode when those rows are hidden
    if (intlVisibility.lockInMarketFrontlines) return;
    setDraft((prev) => ({
      ...prev,
      showInMarketFrontlines: checked,
      // turning off in-market automatically switches to international mode
      showInternationalFrontlines: checked ? false : true,
      // auto-enable downlines when switching to international mode; preserve in in-market mode
      showInternationalDownlines: checked ? prev.showInternationalDownlines : true,
    }));
  };

  // ── Row 3 — Show International Downlines ─────────────────────────────────────
  // Available whenever either frontlines toggle is ON (i.e. always).
  const handleIntlDownlinesChange = (checked: boolean) => {
    setDraft((prev) => ({ ...prev, showInternationalDownlines: checked }));
  };

  // ── Row 4 — Show Data (volume) ────────────────────────────────────────────────
  const handleShowDataChange = (checked: boolean) => {
    setDraft((prev) => ({ ...prev, showData: checked }));
  };

  // ── Row 5 — Show ABOs With Zero Volume ───────────────────────────────────────
  const handleShowZeroVolumeChange = (checked: boolean) => {
    setDraft((prev) => ({ ...prev, showABOsWithZeroVolume: checked }));
  };

  // ── Apply / Cancel ────────────────────────────────────────────────────────────

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!show) return null;

  const showVolume = !!uiHeader.uiEnableFilterVolume;
  const showZeroVolume = !!uiHeader.uiEnableFilterZeroVolume;

  // Apply is disabled when draft has not changed from committed state
  const applyDisabled = statesEqual(draft, filterState);
  // Row 3 is always enabled — downlines can be toggled regardless of which frontlines mode is active

  const title = t("filterModal.title", defaultContent.filterModal.title);
  const labelIntlFrontlines = t(
    "filterModal.showInternationalFrontlinesOnly",
    defaultContent.filterModal.showInternationalFrontlinesOnly,
  );
  const labelInMarketFrontlines = t(
    "filterModal.showInMarketFrontlinesOnly",
    defaultContent.filterModal.showInMarketFrontlinesOnly,
  );
  const labelIntlDownlines = t(
    "filterModal.showInternationalDownlines",
    defaultContent.filterModal.showInternationalDownlines,
  );
  const labelShowData = t("filterModal.showData", defaultContent.filterModal.showData);
  const labelShowZeroVolume = t(
    "filterModal.showABOsWithZeroVolume",
    defaultContent.filterModal.showABOsWithZeroVolume,
  );
  const labelCancel = t("filterModal.cancel", defaultContent.filterModal.cancel);
  const labelApply = t("filterModal.apply", defaultContent.filterModal.apply);

  const modalContent = (
    <>
      <div className="LosFilterModal__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="LosFilterModal" ref={cardRef} role="dialog" aria-modal="true" aria-label={title}>
        <div className="LosFilterModal__header">
          <span className="LosFilterModal__title">{title}</span>
          <button
            className="LosFilterModal__closeButton"
            onClick={onClose}
            type="button"
            aria-label={t("filterModal.close", defaultContent.filterModal.close)}
          >
            <IconClose width="1rem" height="1rem" />
          </button>
        </div>

        <div className="LosFilterModal__body">
          {/* Row 1 — Show International Frontlines Only */}
          {intlVisibility.showIntlFrontlines && (
            <div className="LosFilterModal__row">
              <span className="LosFilterModal__label">{labelIntlFrontlines}</span>
              <LeafToggleSwitch
                isToggled={draft.showInternationalFrontlines}
                onToggle={(e) => handleIntlFrontlinesChange(e.target.checked)}
                name={labelIntlFrontlines}
                toggleOnIcon={<IconCheck color="blue" />}
              />
            </div>
          )}

          {/* Row 2 — Show In-Market Frontlines Only (locked ON when it is the only international row) */}
          {intlVisibility.showInMarketFrontlines && (
            <div className="LosFilterModal__row">
              <span className="LosFilterModal__label">{labelInMarketFrontlines}</span>
              <LeafToggleSwitch
                isToggled={intlVisibility.lockInMarketFrontlines || draft.showInMarketFrontlines}
                onToggle={(e) => handleInMarketFrontlinesChange(e.target.checked)}
                name={labelInMarketFrontlines}
                toggleOnIcon={<IconCheck color="blue" />}
                disabled={intlVisibility.lockInMarketFrontlines}
              />
            </div>
          )}

          {/* Row 3 — Show International Downlines (available in both frontlines modes) */}
          {intlVisibility.showIntlDownlines && (
            <div className="LosFilterModal__row">
              <span className="LosFilterModal__label">{labelIntlDownlines}</span>
              <LeafToggleSwitch
                isToggled={draft.showInternationalDownlines}
                onToggle={(e) => handleIntlDownlinesChange(e.target.checked)}
                name={labelIntlDownlines}
                toggleOnIcon={<IconCheck color="blue" />}
              />
            </div>
          )}

          {/* Row 4 — Show Data */}
          {showVolume && (
            <div className="LosFilterModal__row">
              <span className="LosFilterModal__label">{labelShowData}</span>
              <LeafToggleSwitch
                isToggled={draft.showData}
                onToggle={(e) => handleShowDataChange(e.target.checked)}
                name={labelShowData}
                toggleOnIcon={<IconCheck color="blue" />}
              />
            </div>
          )}

          {/* Row 5 — Show ABOs With Zero Volume */}
          {showZeroVolume && (
            <div className="LosFilterModal__row">
              <span className="LosFilterModal__label">{labelShowZeroVolume}</span>
              <LeafToggleSwitch
                isToggled={draft.showABOsWithZeroVolume}
                onToggle={(e) => handleShowZeroVolumeChange(e.target.checked)}
                name={labelShowZeroVolume}
                toggleOnIcon={<IconCheck color="blue" />}
              />
            </div>
          )}
        </div>

        <div className="LosFilterModal__footer">
          <button className="LosFilterModal__cancelButton" onClick={handleCancel} type="button">
            {labelCancel}
          </button>
          <button className="LosFilterModal__applyButton" onClick={handleApply} disabled={applyDisabled} type="button">
            {labelApply}
          </button>
        </div>
      </div>
    </>
  );

  return isMobile ? ReactDOM.createPortal(modalContent, portalTarget) : modalContent;
};
